import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUrlFilters } from '@/lib/hooks/useUrlFilters';
import type { MatterFilters } from '../types';

const defaults: MatterFilters = {
  s: undefined,
  limit: 10,
  page: 0,
  orderBy: 'createdAt',
  order: 'desc',
  'dueAt.min': undefined,
  'dueAt.max': undefined,
  clientId: undefined,
  userId: undefined,
  contractId: undefined,
  practiceAreaId: undefined,
  phase: undefined,
  phases: undefined,
  result: undefined,
  compliance: undefined,
  isCourtCase: undefined,
};

const numberFields: (keyof MatterFilters)[] = [
  'limit', 'page', 'clientId', 'userId', 'contractId', 'practiceAreaId',
];

const booleanFields: (keyof MatterFilters)[] = ['isCourtCase'];

export function useMatterFilters() {
  const [, setSearchParams] = useSearchParams();
  const { updateFilter: baseUpdateFilter, ...rest } = useUrlFilters<MatterFilters>({
    defaults,
    numberFields,
    booleanFields,
  });

  // Auto-reset page when any filter other than page changes
  const updateFilter = useCallback(
    <K extends keyof MatterFilters>(key: K, value: MatterFilters[K]) => {
      baseUpdateFilter(key, value, key !== 'page');
    },
    [baseUpdateFilter]
  );

  const setSort = useCallback((orderBy: string, order: 'asc' | 'desc') => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('orderBy', orderBy);
      newParams.set('order', order);
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleSort = useCallback((column: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      const currentOrderBy = prev.get('orderBy');
      const currentOrder = prev.get('order');

      if (currentOrderBy === column) {
        newParams.set('order', currentOrder === 'asc' ? 'desc' : 'asc');
      } else {
        newParams.set('orderBy', column);
        newParams.set('order', 'desc');
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  return { ...rest, updateFilter, setSort, toggleSort };
}
